import React from 'react'

import PzDetailsDialog from '@/app/component/atom/pz-details-dialog'
import { getPzCatalog } from '@/app/component/atom/pz-metadata'
import PzPill from '@/app/component/atom/pz-pill'
import { generatePattern } from '@/app/module/utils/personalization'
import { PersonalizationType } from '@/app/types'

import InputHighlight from './input-highlight'

type Props = Omit<
  React.ComponentProps<typeof InputHighlight>,
  'HighlightComponent' | 'highlightPattern' | 'isMatch'
> & {
  personalizationList: PersonalizationType
}

// Makes {{label}} text inspectable through pills and a details dialog; callers handle storage conversions.
const PersonalizationInput: React.FC<Props> = ({ personalizationList, ...props }) => {
  const catalog = React.useMemo(() => getPzCatalog(personalizationList), [personalizationList])
  const byLabel = React.useMemo(() => new Map(catalog.map((item) => [`{{${item.label}}}`, item])), [catalog])
  const pattern = React.useMemo(() => generatePattern(catalog), [catalog])
  const [selectedMatch, setSelectedMatch] = React.useState<string>()
  const trigger = React.useRef<HTMLElement | null>(null)
  const isMatch = React.useCallback((match: string) => byLabel.has(match), [byLabel])
  // Renders each matched label as a pill that opens its catalog details.
  const Highlight = React.useCallback(
    ({ match }: { index: number; match: string }) => {
      const item = byLabel.get(match)
      return item ? (
        <PzPill
          label={item.label}
          onInspect={(element) => {
            trigger.current = element
            setSelectedMatch(match)
          }}
        />
      ) : (
        <span>{match}</span>
      )
    },
    [byLabel],
  )

  return (
    <>
      <InputHighlight {...props} HighlightComponent={Highlight} highlightPattern={pattern} isMatch={isMatch} />
      <PzDetailsDialog
        details={selectedMatch === undefined ? undefined : byLabel.get(selectedMatch)}
        trigger={trigger.current}
        getFallbackFocus={() => props.inputRef.current}
        onClose={() => setSelectedMatch(undefined)}
      />
    </>
  )
}

export default PersonalizationInput
